/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useRouter } from "@i18n/next";
import {
  numericPercentages,
  radius,
  Slider,
  spacing,
  zIndices,
} from "@packages/ui";
import { NProgress } from "@tanem/react-nprogress";
import { useEffect, useOptimistic, useTransition } from "react";

const sliderStyle: Interpolation = {
  height: spacing.xxs,
  left: 0,
  pointerEvents: "none",
  position: "fixed",
  right: 0,
  top: 0,
  width: numericPercentages[100],
  zIndex: zIndices.loading,
};
const doneStyle: Interpolation = {
  display: "none",
};
const noRadiusStyle: Interpolation = {
  borderRadius: radius.none,
};

export const NavigationLoading = () => {
  const router = useRouter();
  // useOptimistic is a useState hook that reverts to the optimistic (given -- here, false) state
  // whenever an action completes
  const [loading, setLoading] = useOptimistic<boolean>(false);
  const [, startTransition] = useTransition();

  useEffect(() => {
    // patch the router.push function to enable showing the loading state
    if (router.push.name === "patched") return; // we've already done this
    const push = router.push;
    router.push = function patched(...args) {
      // we shouldn't need to start a transition here because App Router navigations are always inside transitions
      // but an error keeps getting thrown that says we should
      startTransition(() => setLoading(true));
      return push.apply(history, args);
    };
  }, []);

  const popStateHandler = () => {
    startTransition(() => setLoading(true));
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("popstate", popStateHandler);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("popstate", popStateHandler);
      }
    };
  }, []);

  return (
    <NProgress isAnimating={loading}>
      {({ progress, isFinished }) => (
        <Slider
          value={progress * 100}
          css={[sliderStyle, isFinished && doneStyle]}
          size={"small"}
          color="accentHigh"
          rangeCSS={noRadiusStyle}
          trackCSS={noRadiusStyle}
        />
      )}
    </NProgress>
  );
};
